import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Store, ArrowLeft, ArrowRight, LogOut, ShoppingCart, MapPin, MailPlus, Loader2, Link2 } from "lucide-react";

export default function PurchasesHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);

  useEffect(() => {
    api.get("/purchases/stores").then((r) => setStores(r.data.stores)).finally(() => setLoading(false));
  }, []);

  const fetchRecent = async () => {
    setFetching(true);
    setFetchResult(null);
    try {
      const r = await api.post("/purchases/vtec/fetch-recent?days=14");
      const d = r.data;
      setFetchResult(d);
      if (d.found === 0) toast.info("Geen VTEC-rapporten van de laatste 14 dagen in de mailbox gevonden.");
      else toast.success(`Mailbox: ${d.imported} nieuw geïmporteerd, ${d.duplicate} al aanwezig${d.failed ? `, ${d.failed} mislukt` : ""}.`);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Ophalen mislukt"); }
    finally { setFetching(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="purchases-hub">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-amber-600 flex items-center justify-center text-white">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm">Purchases — Rookwaren</div>
              <div className="text-xs text-muted-foreground">Colruyt Professionals bestelsysteem</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" data-testid="purchases-back-hub" onClick={() => navigate("/hub")}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Modules
            </Button>
            <Button variant="ghost" size="sm" data-testid="purchases-logout" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Kies een winkel</h1>
        <p className="text-muted-foreground mt-2">Voor welke winkel wil je de verkoop bekijken en bestellen?</p>

        {user?.role === "admin" && (
          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <Button variant="outline" size="sm" disabled={fetching} data-testid="fetch-recent-btn" onClick={fetchRecent}>
              {fetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailPlus className="h-4 w-4 mr-2" />} Haal laatste 14 dagen op uit mailbox
            </Button>
            <Button variant="ghost" size="sm" data-testid="hub-mappings-btn" onClick={() => navigate("/purchases/koppelingen")}>
              <Link2 className="h-4 w-4 mr-2" /> Koppelingen
            </Button>
          </div>
        )}

        {fetchResult && (
          <div className="mt-4 rounded-xl border border-border bg-card p-4" data-testid="fetch-recent-result">
            <div className="text-sm font-medium mb-2">
              Mailbox-resultaat: {fetchResult.found} bestand(en) gevonden · {fetchResult.imported} geïmporteerd · {fetchResult.duplicate} al aanwezig · {fetchResult.failed} mislukt
            </div>
            {(fetchResult.details || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left">
                      <th className="py-1 pr-3">Bestand</th>
                      <th className="py-1 pr-3">Status</th>
                      <th className="py-1 pr-3">Winkel</th>
                      <th className="py-1 pr-3">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fetchResult.details.map((it, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1 pr-3 max-w-md truncate" title={it.filename}>{it.filename}</td>
                        <td className={`py-1 pr-3 font-medium ${it.status === "IMPORTED" ? "text-emerald-600" : it.status === "DUPLICATE" ? "text-muted-foreground" : "text-destructive"}`}>{it.status}{it.error ? ` — ${it.error}` : ""}</td>
                        <td className="py-1 pr-3">{it.store_code || "—"}</td>
                        <td className="py-1 pr-3 font-mono">{it.report_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Geen "Gemiste verkopen"-bestanden gevonden in de laatste 14 dagen. Controleer of die mails in het postvak van de app staan.</p>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground mt-10">Laden…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {stores.map((s) => (
              <button
                key={s.code}
                data-testid={`purchases-store-${s.code}`}
                onClick={() => navigate(`/purchases/${s.code}`)}
                className="group text-left rounded-2xl border border-border bg-card p-7 shadow-sm hover:shadow-md hover:border-amber-600 transition-all duration-200 active:scale-[0.99]"
              >
                <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-amber-600">
                  <Store className="h-6 w-6 text-white" />
                </div>
                <div className="mt-5 inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{s.code}</div>
                <h2 className="font-heading text-xl font-semibold tracking-tight mt-2">{s.name}</h2>
                <p className="text-sm text-muted-foreground mt-2 flex items-start gap-1.5">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {s.address_line}, {s.postal_code} {s.city}
                </p>
                <div className="flex items-center gap-1.5 text-sm font-medium text-amber-700 mt-5 group-hover:gap-2.5 transition-all">
                  Openen <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
